import { A } from '@solidjs/router';
import { createEffect, createSignal, onCleanup, Show } from 'solid-js';
import PublicLayout from '../PublicLayout';

export const POST_META = {
  slug: 'growth-trap',
  title:
    'The Growth Trap: Why Your Manual Systems Will Break at 50 Orders a Day',
  description:
    'What works at 5 orders a day becomes a nightmare at 50. Learn about "technical debt" in the warehouse, why barcode scanning is the single biggest accuracy upgrade you can make, and how the 60-second stress test reveals whether your operation is truly ready to scale.',
  date: 'May 8, 2026',
  dateIso: '2026-05-08',
  readTime: '8 min read',
  category: 'Operations Strategy',
  heroImage: '/blog-scaling-chaos.jpg',
  ogImage: '/blog-scaling-chaos.jpg',
};

export default function GrowthTrapPost() {
  const [heroFailed, setHeroFailed] = createSignal(false);
  const [barcodeImageFailed, setBarcodeImageFailed] = createSignal(false);
  const [chartFailed, setChartFailed] = createSignal(false);

  createEffect(() => {
    const schema = {
      '@context': 'https://schema.org',
      '@type': 'BlogPosting',
      headline: POST_META.title,
      description: POST_META.description,
      datePublished: POST_META.dateIso,
      dateModified: POST_META.dateIso,
      image: `https://khoai.io${POST_META.heroImage}`,
      url: 'https://khoai.io/blogs/growth-trap',
      inLanguage: 'en',
      author: {
        '@type': 'Organization',
        name: 'khoai.io',
        url: 'https://khoai.io',
      },
      publisher: {
        '@type': 'Organization',
        name: 'khoai.io',
        url: 'https://khoai.io',
        logo: {
          '@type': 'ImageObject',
          url: 'https://khoai.io/og-welcome.jpg',
        },
      },
      isPartOf: {
        '@type': 'Blog',
        name: 'Khoai Blog',
        url: 'https://khoai.io/blogs',
      },
    };

    let script = document.getElementById(
      'ld-json-blog-growth-trap'
    ) as HTMLScriptElement | null;
    if (!script) {
      script = document.createElement('script');
      script.type = 'application/ld+json';
      script.id = 'ld-json-blog-growth-trap';
      document.head.appendChild(script);
    }
    script.textContent = JSON.stringify(schema);

    let bcScript = document.getElementById(
      'ld-json-blog-growth-trap-bc'
    ) as HTMLScriptElement | null;
    if (!bcScript) {
      bcScript = document.createElement('script');
      bcScript.type = 'application/ld+json';
      bcScript.id = 'ld-json-blog-growth-trap-bc';
      document.head.appendChild(bcScript);
    }
    bcScript.textContent = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        {
          '@type': 'ListItem',
          position: 1,
          name: 'Home',
          item: 'https://khoai.io',
        },
        {
          '@type': 'ListItem',
          position: 2,
          name: 'Blog',
          item: 'https://khoai.io/blogs',
        },
        {
          '@type': 'ListItem',
          position: 3,
          name: schema.headline,
          item: schema.url,
        },
      ],
    });
  });

  onCleanup(() => {
    document.getElementById('ld-json-blog-growth-trap')?.remove();
    document.getElementById('ld-json-blog-growth-trap-bc')?.remove();
  });

  // Shared prose classes
  const h2 = 'mt-12 text-2xl font-extrabold text-gray-900 sm:text-3xl';
  const p = 'mt-4 text-base leading-8 text-gray-600';
  const ul = 'mt-4 list-disc space-y-2 pl-6 text-base leading-7 text-gray-600';
  const imgFallback =
    'flex min-h-[320px] w-full items-center justify-center rounded-2xl bg-gray-100 text-sm text-gray-400';

  return (
    <PublicLayout>
      <article class="mx-auto max-w-3xl px-4 py-16 sm:px-6 lg:px-8">
        {/* Breadcrumb */}
        <nav class="mb-8 flex items-center gap-2 text-sm text-gray-400">
          <A href="/blogs" class="hover:text-amber-600">
            Blog
          </A>
          <span>/</span>
          <span class="text-gray-600">Operations Strategy</span>
        </nav>

        {/* Header */}
        <header>
          <span class="rounded-full bg-amber-100 px-3 py-0.5 text-xs font-semibold text-amber-700">
            {POST_META.category}
          </span>
          <h1 class="mt-4 text-3xl font-extrabold leading-tight text-gray-900 sm:text-4xl lg:text-5xl">
            {POST_META.title}
          </h1>
          <p class="mt-4 text-lg leading-relaxed text-gray-500">
            {POST_META.description}
          </p>
          <div class="mt-5 flex items-center gap-4 text-sm text-gray-400">
            <span>{POST_META.date}</span>
            <span>·</span>
            <span>{POST_META.readTime}</span>
          </div>
        </header>

        {/* Hero image — 1200×628 */}
        <div class="mt-10 overflow-hidden rounded-2xl">
          <Show
            when={!heroFailed()}
            fallback={
              <div class={imgFallback} style={{ 'min-height': '340px' }}>
                Split screen: messy paper order desk vs. clean tablet pick-list
              </div>
            }
          >
            <img
              src="/blog-scaling-chaos.jpg"
              alt="Split screen: left side shows a cluttered desk with piles of paper orders; right side shows a clean tablet interface with a clear digital pick list"
              width={1200}
              height={628}
              class="w-full object-cover"
              onError={() => setHeroFailed(true)}
            />
          </Show>
        </div>

        {/* ── Body ── */}
        <div class="mt-12">
          {/* Hook: Monday Morning Chaos */}
          <p class={p}>
            It&apos;s 7 AM Monday. Your team walks into the warehouse. The
            weekend delivered more than 50 new orders — more than a full normal
            week from just two months ago. Nobody knows where to start. The pick
            list printed Friday night is already outdated; 12 more orders came
            in after midnight. And the one warehouse employee who knows where
            everything lives just called in sick.
          </p>
          <p class={p}>
            This isn&apos;t a disaster scenario. This is{' '}
            <strong class="font-semibold text-gray-900">
              the breaking point
            </strong>{' '}
            — the moment your operational systems, which worked fine at small
            scale, suddenly can&apos;t carry the weight you need them to.
          </p>
          <p class={p}>
            The unsettling part: this point arrives earlier than most founders
            expect, and almost always in the middle of their best growth
            stretch.
          </p>

          {/* Section 1: Brain Drain */}
          <h2 class={h2}>
            The &ldquo;Knowledge Lives in One Person&rdquo; Problem
          </h2>
          <p class={p}>
            In small operations, there&apos;s usually one person who knows
            everything: SKU 0047 lives in the back-left corner of Bay B,
            Supplier X tends to ship short by 5%, and Client Y only accepts
            deliveries in the afternoon. That knowledge exists nowhere except
            inside that person&apos;s head.
          </p>
          <p class={p}>
            At five orders a day, this is fine. At fifty orders a day — with 300
            active SKUs, four warehouse staff who need to work in parallel, and
            a new hire starting next week — it&apos;s a single point of failure
            dressed up as operational knowledge.
          </p>

          {/* Signs You're Already There */}
          <div class="mt-8 rounded-2xl border border-gray-200 bg-gray-50 p-6">
            <p class="text-sm font-bold uppercase tracking-wide text-gray-500">
              Signs you&apos;re already there
            </p>
            <ul class="mt-3 space-y-2 text-base leading-7 text-gray-700">
              <li>✗ You&apos;ve lost or delayed an order this month</li>
              <li>
                ✗ When a key employee calls in sick, the warehouse visibly slows
                down
              </li>
              <li>
                ✗ You can&apos;t confidently answer &ldquo;do we have it in
                stock?&rdquo; in real time
              </li>
              <li>
                ✗ Month-end stock reconciliation takes one to two full days
              </li>
              <li>
                ✗ You&apos;ve shipped the wrong item because two SKUs looked
                similar on the shelf
              </li>
            </ul>
          </div>

          <p class={p}>
            The solution isn&apos;t to find a better employee. The solution is
            to{' '}
            <strong class="font-semibold text-gray-900">
              move that knowledge out of someone&apos;s head and into the system
            </strong>
            . Labeled bin locations (A-01-3, B-12-2). Standardized pick
            sequences. A single searchable product catalog that anyone —
            including a first-day hire — can navigate without a guide.
          </p>

          {/* Section 2: Error Multiplier */}
          <h2 class={h2}>
            The Math of Mistakes: A 2% Error Rate Sounds Small Until You Run the
            Numbers
          </h2>
          <p class={p}>
            At five orders a day, a 2% pick error rate means one wrong shipment
            roughly every ten days. Embarrassing, but manageable — an apology, a
            replacement, and you move on.
          </p>
          <p class={p}>
            At fifty orders a day with that same 2% rate: one angry customer
            every single day. Not every week. Every day. Multiply that by return
            shipping costs, re-delivery expense, customer service time, and
            brand damage — and suddenly a &ldquo;small&rdquo; error rate is
            eating real margin.
          </p>
          <p class={p}>
            Manual warehouses don&apos;t make more mistakes because they hire
            worse people. They make more mistakes because of the sheer{' '}
            <strong class="font-semibold text-gray-900">
              number of visual decisions required per shift
            </strong>
            : reading a label, matching a SKU code, verifying a bin location,
            confirming a quantity. Each step is a chance to slip. When your
            volume is ten times higher, so is your opportunity for error.
          </p>

          {/* Section 3: Barcode */}
          <h2 class={h2}>
            Barcode Scanning: Not Intimidating Hardware — The Single Biggest
            Accuracy Upgrade You Can Make
          </h2>
          <p class={p}>
            Most small warehouse owners assume barcode scanning means expensive
            dedicated scanners, complex setup, and an IT project. It
            doesn&apos;t. Khoai works with any smartphone camera — no additional
            hardware required.
          </p>
          <p class={p}>
            The accuracy gap between &ldquo;reading a label by eye&rdquo; and
            &ldquo;scanning a barcode&rdquo; sounds incremental. It is not:
          </p>
          <ul class={ul}>
            <li>
              <strong class="font-semibold text-gray-900">
                Reading by eye:
              </strong>{' '}
              human error rates of 1–3%, compounding under fatigue and time
              pressure
            </li>
            <li>
              <strong class="font-semibold text-gray-900">Barcode scan:</strong>{' '}
              error rates below 0.01% — functionally zero
            </li>
          </ul>
          <p class={p}>
            When you scan, the system confirms instantly: correct product,
            correct quantity, correct bin. There&apos;s no room for &ldquo;I
            thought it was...&rdquo; or &ldquo;they look the same.&rdquo; The
            software catches mistakes before the order leaves your building.
          </p>

          {/* Barcode image — 800×500 */}
          <div class="mt-10 overflow-hidden rounded-2xl">
            <Show
              when={!barcodeImageFailed()}
              fallback={
                <div class={imgFallback} style={{ 'min-height': '280px' }}>
                  Hand scanning a barcode label on a warehouse rack with a
                  smartphone
                </div>
              }
            >
              <img
                src="/blog-barcode-scanning.jpg"
                alt="A hand using a smartphone to scan a professional barcode label on a warehouse rack"
                width={800}
                height={500}
                class="w-full object-cover"
                loading="lazy"
                onError={() => setBarcodeImageFailed(true)}
              />
            </Show>
          </div>

          {/* Section 4: Messy Middle */}
          <h2 class={h2}>
            The &ldquo;Messy Middle&rdquo;: Too Big for a Spreadsheet, Feels Too
            Small for &ldquo;Real&rdquo; Software
          </h2>
          <p class={p}>
            This is the hardest phase: you&apos;re processing 30–60 orders a
            day, spreadsheets are starting to cause headaches, but you still
            feel like you&apos;re not quite big enough to justify a
            &ldquo;proper&rdquo; system. Enterprise WMS platforms feel designed
            for Amazon-scale operations, not for you.
          </p>
          <p class={p}>
            This is exactly where most growing businesses get stuck. They wait
            until the pain is bad enough to force action — but by then, they
            &apos;ve already lost customers, burned out their best staff, and
            accumulated months of messy data that needs cleaning up before the
            new system can even start.
          </p>

          {/* Khoai callout */}
          <div class="mt-8 rounded-2xl border border-amber-200 bg-amber-50 p-6">
            <p class="text-base leading-8 text-gray-700">
              <strong class="font-semibold text-gray-900">
                Khoai was built specifically for this phase.
              </strong>{' '}
              Not a bloated enterprise WMS with hundreds of features you
              don&apos;t need. Not a spreadsheet that will snap under pressure.
              Khoai is operational in hours, requires no implementation
              consultant, and scales with you from 30 to 300 orders a day
              without a system change. You bring your team — Khoai brings the
              structure.
            </p>
          </div>

          {/* Section 5: Stress Test */}
          <h2 class={h2}>The 60-Second Stress Test</h2>
          <p class={p}>
            Here&apos;s the simplest way to know whether your warehouse
            operation is genuinely ready to scale:
          </p>
          <div class="mt-6 rounded-2xl border-2 border-amber-400 bg-amber-50 p-6">
            <p class="text-lg font-bold text-gray-900">
              The challenge: could a stranger — someone who has never set foot
              in your warehouse — locate a specific SKU in under 60 seconds?
            </p>
            <p class="mt-3 text-base text-gray-600">
              If the answer is &ldquo;no&rdquo; or &ldquo;only if [name] is
              here&rdquo; — you&apos;re running a person-dependent business, not
              a process-dependent one. That distinction will cost you when
              growth accelerates.
            </p>
          </div>
          <p class={p}>
            If your system passes — bin locations are clearly labeled, the
            product catalog is searchable, any team member can pick from a list
            — you&apos;re building something that can scale. If it doesn&apos;t,
            you&apos;re building a trap for your future self.
          </p>

          {/* Growth chart image — 800×500 */}
          <div class="mt-10 overflow-hidden rounded-2xl">
            <Show
              when={!chartFailed()}
              fallback={
                <div class={imgFallback} style={{ 'min-height': '280px' }}>
                  Growth chart showing an upward arrow passing a Breaking Point
                  line, continuing up labeled Optimized
                </div>
              }
            >
              <img
                src="/blog-growth-chart.jpg"
                alt="Clean growth chart with an upward arrow passing a Breaking Point line at 50 orders/day, continuing up with an Optimized label"
                width={800}
                height={500}
                class="w-full object-cover"
                loading="lazy"
                onError={() => setChartFailed(true)}
              />
            </Show>
          </div>

          {/* Section 6: SOPs */}
          <h2 class={h2}>
            Software Doesn&apos;t Just Automate — It Forces You to Build
            Processes
          </h2>
          <p class={p}>
            One of the least-discussed benefits of moving to a warehouse
            management platform is that it forces you to make decisions you
            &apos;ve been deferring. When you enter a product into the system,
            you have to decide: what is the SKU? Where does it live? What&apos;s
            the unit of measure? What&apos;s the reorder threshold?
          </p>
          <p class={p}>
            Those decisions — once recorded in the system — become your standard
            operating procedures. Not a document someone wrote and nobody reads.
            Living procedures, enforced every time anyone scans a barcode or
            confirms a pick.
          </p>
          <p class={p}>
            That&apos;s how you make the shift from a person-dependent business
            to a process-dependent one. And only once you&apos;ve made that
            shift are you genuinely ready to grow.
          </p>

          {/* CTA */}
          <div class="mt-14 rounded-2xl bg-amber-600 px-8 py-10 text-center text-white">
            <h2 class="text-2xl font-extrabold">
              Stuck in the messy middle? Here&apos;s your way out.
            </h2>
            <p class="mt-3 text-base leading-7 text-amber-100">
              Khoai gives you labeled bin locations, barcode scanning on any
              phone, and the structure to handle 300 orders a day with the team
              you have right now. Start free — no credit card, no consultant
              required.
            </p>
            <A
              href="/signup"
              class="mt-6 inline-block rounded-xl bg-white px-8 py-3 text-base font-bold text-amber-700 hover:bg-amber-50"
            >
              Try Khoai free →
            </A>
          </div>
        </div>

        {/* Back to blog */}
        <div class="mt-16 border-t border-gray-200 pt-8">
          <A
            href="/blogs"
            class="text-sm font-semibold text-amber-600 hover:text-amber-700"
          >
            ← Back to all posts
          </A>
        </div>
      </article>
    </PublicLayout>
  );
}
